import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, skipWhile, take, tap } from 'rxjs/operators';
import { PolicyApplication, ResidentialService } from '../core/services/residential.service';
import { BrandingService } from '../core/services/branding.service';
import { AuthService } from '../core/services';
import { FormsService } from '../shared/forms.service';
import * as _ from 'lodash';
import { ResidentialFormComponent } from './residential-form/residential-form.component';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { ResidentialLeadService } from './residential-lead.service';
import { GaEcommerceService } from '../core/services/ga-ecommerce.service';
import { AppService } from '../core/services/app.service';
import { DataLayerResidentialService } from '../core/services/data-layer-residential.service';
import { ConvertCoveragesService } from '../core/services/convert-coverages.service';
import { PmaService } from '../pma/pma.service';
import { EstimatedPremiumLead } from '../shared/estimated-premium/estimated-premium.component';

@Component({
  selector: 'app-residential',
  templateUrl: './residential.component.html',
  styleUrls: ['./residential.component.scss'],
})
export class ResidentialComponent implements OnInit, AfterViewInit {
  residential: any;
  formData: any;
  showStripe = false;
  secondResidentialObj: PolicyApplication;
  loader = false;
  multiStepForm = 1;
  policyApplicationId = null;
  carrierId: number;
  totalPremium;
  period: string;
  selectedPaymentPlan;
  addressData;
  paymentLoading = false;
  communitySpecificAddress;
  communitySpecificSelectedCommunity;
  private coverageOptionsWasInited = false;
  @ViewChild(ResidentialFormComponent) residentialFormComponent: ResidentialFormComponent;

  constructor(
    public brandingService: BrandingService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private formsService: FormsService,
    private resService: ResidentialService,
    private residentialLeadService: ResidentialLeadService,
    private router: Router,
    private toastr: ToastrService,
    private gaEcommerceService: GaEcommerceService,
    private appService: AppService,
    private dataLayerResidentialService: DataLayerResidentialService,
    private convertCoveragesService: ConvertCoveragesService,
    private pmaService: PmaService
  ) {
  }

  ngOnInit() {
    this.setCommunitySpecificAddressFromQuery();

    this.brandingService.setBrandingTitle(`Renters Insurance`);

    const policyId = this.route.snapshot.params.id;

    if (policyId) {
      this.resService.getPublicPolicyApplicationById(policyId).subscribe(result => {
        if (result.policy_type_id !== 1) {
          this.router.navigateByUrl('residential').then(() => {
            this.toastr.error('Invalid address');
          });
        } else {
          this.initDataFromServer(result);
        }
      }, (err) => {
        this.formsService.handleFormErrors(err);
      });
    }

    this.appService.isGaLoaded$.pipe(
      skipWhile((isGaLoaded) => !isGaLoaded)
    ).subscribe(() => {
      window.gtag('event', 'view_item', {
          currency: 'USD',
          items: [this.gaEcommerceService.getProduct(1)]
      });
    });
  }

  setCommunitySpecificAddressFromQuery() {
    this.route.queryParams.subscribe(params => {
      const { community_id } = params;
      if (community_id) {
        this.resService.getInsurableById(community_id).subscribe((insurable: any) => {
          this.communitySpecificSelectedCommunity = insurable;
          const addressAttributes = insurable && insurable.addresses_attributes && insurable.addresses_attributes[0] ? insurable.addresses_attributes[0] : null;
          const address = addressAttributes && addressAttributes.full ?  addressAttributes.full : null;
          if (address) {
            this.communitySpecificAddress = address;
          }
        });
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const policyId = this.route.snapshot.params.id;

      if (policyId) {
        return;
      }

      this.brandingService.brandingInfo$.subscribe(brandingInfo => {
        this.patchFormLocalStorage(brandingInfo);
        this.prefillCommunityFromCommunitySpecificUrl(brandingInfo);
      });
    }, 500);
  }

  patchFormLocalStorage(brandingInfo) {
    const RENTERS_LOCAL_STORAGE_KEY = ResidentialService.RENTERS_LOCAL_STORAGE_KEY;

    if (localStorage.getItem(RENTERS_LOCAL_STORAGE_KEY)) {

      const formData = JSON.parse(localStorage.getItem(RENTERS_LOCAL_STORAGE_KEY));
      delete formData.billing_strategy_id;

      const county = localStorage.getItem('RENTERS_COUNTY');
      if (county) {
        this.residentialFormComponent.residentialForm.get('county').patchValue(county);
      }

      const neighborhood = localStorage.getItem('RENTERS_NEIGHBORHOOD');
      if (neighborhood) {
        this.residentialFormComponent.residentialForm.get('neighborhood').patchValue(neighborhood);
      }

      this.residentialFormComponent.residentialForm.patchValue(formData, {emitEvent: false});
      this.residentialFormComponent.residentialForm.controls['pm_account'].updateValueAndValidity();

      this.residentialFormComponent.initForm();
    }
  }

  prefillCommunityFromCommunitySpecificUrl(brandingInfo) {
    const nonpreferredAddressSelection = brandingInfo.profile_attributes.some(item => item.name === 'nonpreferred_address_selection');
    if (nonpreferredAddressSelection && this.communitySpecificAddress) {
      this.residentialFormComponent.residentialForm.get('address').patchValue(this.communitySpecificSelectedCommunity);
    } else if (!nonpreferredAddressSelection && this.communitySpecificSelectedCommunity) {
      this.residentialFormComponent.residentialForm.get('community').patchValue(this.communitySpecificSelectedCommunity);
    }
  }

  coverageOptionsInited() {
    if (!this.coverageOptionsWasInited) {
      this.coverageOptionsWasInited = true;
      setTimeout(() => {
        const RENTERS_LOCAL_STORAGE_KEY = ResidentialService.RENTERS_LOCAL_STORAGE_KEY;
        const formData = JSON.parse(localStorage.getItem(RENTERS_LOCAL_STORAGE_KEY));
        delete formData.billing_strategy_id;
        this.residentialFormComponent.residentialForm.patchValue(formData, {emitEvent: false});
      });
    }
  }

  estimateTotal(total) {
    this.totalPremium = total;
  }

  submitForm(value) {
    window.gtag('event', 'add_to_cart', {
      items: [
        this.gaEcommerceService.getProduct(1, {
          price: this.gaEcommerceService.formatPrice(this.totalPremium)
        })
      ]
    });

    window.gtag('event', 'begin_checkout', {
      currency: 'USD',
      items: [
          this.gaEcommerceService.getProduct(1, {
              price: this.gaEcommerceService.formatPrice(this.totalPremium)
          })
      ]
    });

    const residential = value.residential;
    this.selectedPaymentPlan = value.additionalInformation.billingStrategy;
    this.addressData = {
      email: value.additionalInformation.email,
      ...value.additionalInformation.primaryAddress
    };
    this.loader = true;
    this.carrierId = residential.carrier_id;
    const newResidential = _.merge({}, this.residential, residential);
    newResidential.coverage_selections = this.convertCoveragesService.convertCoverageSelections(newResidential.coverage_selections);
    const serverRequest = this.policyApplicationId
      ? this.resService.updatePolicyApplication(this.policyApplicationId, newResidential)
      : this.resService.postCheckResidential(newResidential);
    serverRequest
      .subscribe(
        result => {
          this.createPolicySubmitEvent();
          this.residential = newResidential;
          this.handleFormSuccess(result);
          if (!this.authService.isAuthenticatedSnapshot) {
            this.authService.authenticateUser();
          }

          this.pmaService.storeInsurableByAuthToken(null);
        },
        e => {
          this.formsService.handleFormErrors(e);
          this.loader = false;
        }
      );
  }

  handleFormSuccess(result: PolicyApplication) {
    this.secondResidentialObj = result;
    this.showStripe = true;
    this.loader = false;
    this.multiStepForm = 2;
    this.policyApplicationId = result.id;
    setTimeout(() => {
      const el = document.getElementById('guaranteeContainer');
      el.scrollIntoView({behavior: 'smooth'});
    }, 0);
  }

  createPolicySubmitEvent() {
    this.residentialLeadService.createPolicySubmitEvent();
  }

  createPaymentSuccessEvent() {
    localStorage.removeItem(ResidentialService.RENTERS_LOCAL_STORAGE_KEY);
    this.residentialLeadService.createPaymentSuccessEvent();
  }

  createPaymentErrorEvent() {
    this.residentialLeadService.createPaymentErrorEvent();
  }

  trackCompleteCard(value) {
    this.residentialLeadService.createCompleteCard(value);
  }

  back() {
    this.residentialLeadService.createBackEvent();
    this.showStripe = false;
    this.multiStepForm = 1;
  }

  changePaymentPlan($event) {
    this.paymentLoading = true;
    this.residential.billing_strategy_id  = $event.id;
    this.resService.updatePolicyApplication(this.policyApplicationId, this.residential).pipe(
      finalize(() => this.paymentLoading = false)
    ).subscribe((res) => {
      this.residentialFormComponent.residentialForm.controls['billing_strategy_id'].setValue($event.title);
      this.secondResidentialObj = res;
      this.selectedPaymentPlan = $event;
    }, (err) => {
      this.formsService.handleFormErrors(err);
    });
  }

  totalChangePaymentLead({monthly, period, total}: EstimatedPremiumLead): void {
    this.residentialLeadService.createTotalChangedLead(monthly, period, total);
  }

  private initDataFromServer(serverData) {
    this.dataLayerResidentialService.formAppendedData();

    this.residential = serverData;
    const policyAttributes = serverData.policy_users_attributes[0] || {};
    const userAttributes = policyAttributes.user_attributes;
    const profileAttributes = userAttributes.profile_attributes;
    const insurableAttributes = serverData.policy_insurables_attributes[0] ? serverData.policy_insurables_attributes[0].insurable_attributes : {};
    const addressAttributes = insurableAttributes.primary_address || {};
    const extraSettings = serverData.extra_settings || {};

    const address = serverData.policy_insurables_attributes.length > 0
        ? this.resService.getFullAddress(addressAttributes) : serverData.resolver_info.address_string;
    const address2 = insurableAttributes.hasOwnProperty('title') ? insurableAttributes.title : serverData.resolver_info.unit_string;

    this.formData = {
      effective_date: moment( serverData.effective_date, 'YYYY-MM-DD').toDate(),
      expiration_date: moment( serverData.expiration_date, 'YYYY-MM-DD').toDate(),
      email: userAttributes.email,
      first_name: profileAttributes.first_name,
      last_name: profileAttributes.last_name,
      birth_date_main : moment( profileAttributes.birth_date, 'YYYY-MM-DD').toDate(),
      address,
      unit: address2,
      contact_phone: profileAttributes.contact_phone,
      street_name:  addressAttributes.street_number,
      street_two: addressAttributes.street_number,
      city: addressAttributes.city,
      state: addressAttributes.state,
      postal_code: addressAttributes.zip_code,
      number_of_insured: serverData.fields.find(item => item.title === 'Number of Insured').value,
      community_details: {
        number_of_units: extraSettings.number_of_units || '',
        gated: extraSettings.gated || '',
        year_built: extraSettings.year_built || '',
        years_professionally_managed: extraSettings.years_professionally_managed || ''
      }
    };
    if (this.residential.carrier_id === 5 && this.residential.coverage_selections) {
      this.initCoverageOptions();
    }
    this.residentialFormComponent.residentialForm.patchValue(this.formData);
    setTimeout(() => {
      this.residentialFormComponent.initForm();
    });
    this.policyApplicationId = serverData.id;
  }

  private initCoverageOptions() {
    const coverageSelections = this.residential.coverage_selections;
    this.residential.coverage_selections = [];
    this.residentialFormComponent.newCarrierFormInited.asObservable().pipe(
      take(1),
      tap(() => {
        const newCarrierOptionsData = coverageSelections.filter(f => !!f.selection).reduce((acc, f) => {
          return {
            ...acc,
            [f.uid]: typeof f.selection === 'boolean' ? f.selection : f.selection.value
          };
        }, {});
        this.residentialFormComponent.residentialForm.controls['new_carrier'].patchValue(newCarrierOptionsData);
      })
    ).subscribe();
  }
}
